import React, { useMemo, useRef, useState } from "react";
import {
  Drawer,
  IconButton,
  TextField,
  Typography,
  Box,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Button,
  CardMedia,
  Menu,
  MenuItem,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import ImageIcon from "@mui/icons-material/Image";
import MovieIcon from "@mui/icons-material/Movie";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import DescriptionIcon from "@mui/icons-material/Description";
import SmartButtonIcon from "@mui/icons-material/SmartButton";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import LinkIcon from "@mui/icons-material/Link";
import SettingsIcon from "@mui/icons-material/Settings";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
 
const DEFAULT_PDFS = [
  {
    title: "BOT BUILDER Help file (S3)",
    href: "https://mdimages-test.s3.amazonaws.com/2bdce5f2-b4d3-47a6-af67-b17070961b48_BOTBUILDERHelpfile.pdf",
  },
];

type PDFLink = { title: string; href: string };
type BotFlowHelpProps = {
  pdfs?: PDFLink[];
  images?: string[];
  nodeImages?: Partial<Record<string, string>>;
};

type StepBlock = { title: string; bullets: string[]; source?: string };
type NodeBlock = {
  title: string;
  icon: React.ReactNode;
  definition: string;
  howItWorks?: string[];
  options?: { label: string; icon?: React.ReactNode; hint?: string }[];
  badge?: string; 
  source?: string;
  tags?: string[];
  previewImageKey?: string; 
};

const GETTING_STARTED_STEPS: StepBlock[] = [
  { title: "Open Flows", bullets: ["Click the Flowchart icon in Machadalo.", "The Flows tab opens."], source: "Help file (1) — Step 1" },
  { title: "Add a Flow", bullets: ["Click the ADD button (top-right).", "A form titled “Add a New Flow” opens."], source: "Help file (1) — Step 2" },
  {
    title: "Fill the Form",
    bullets: [
      "Select Organization.",
      "Select Campaign.",
      "Phone Number auto-fills after choosing organization.",
      "Enter Flow Name.",
      "Enter a unique Keyword (must not clash with existing flows).",
    ],
    source: "Help file (1) — Step 2 details",
  },
  { title: "Create Flow", bullets: ["Click Create Flow — your flow is ready."], source: "Help file (1) — Step 3" },
];

const NODE_CARDS: NodeBlock[] = [
  {
    title: "Text",
    icon: <TextFieldsIcon />,
    definition: "Delivers plain text to the user—no media/interactive elements.",
    howItWorks: ["Use for short instructions, confirmations, simple prompts."],
    badge: "Basic",
    source: "Help file — Nodes (p.1)",
    tags: ["message", "simple"],
    previewImageKey: "Text",
  },
  {
    title: "Image + Text",
    icon: <ImageIcon />,
    definition: "Combines an image with supporting text.",
    howItWorks: ["Great for visuals with context or guidance."],
    badge: "Basic",
    source: "Help file — Nodes (p.2)",
    tags: ["visual"],
    previewImageKey: "Image + Text",
  },
  {
    title: "Video + Text",
    icon: <MovieIcon />,
    definition: "Plays a video clip with descriptive/guiding text.",
    howItWorks: ["User taps to play; text provides captions/instructions."],
    badge: "Basic",
    source: "Help file — Nodes (p.3)",
    tags: ["media"],
    previewImageKey: "Video + Text",
  },
  {
    title: "Audio",
    icon: <AudiotrackIcon />,
    definition: "Shares an audio clip with optional text.",
    howItWorks: ["User can play/pause; text may include a short transcript."],
    badge: "Basic",
    source: "Help file — Nodes (p.3)",
    tags: ["media"],
    previewImageKey: "Audio",
  },
  {
    title: "Documents + Text",
    icon: <DescriptionIcon />,
    definition: "Sends a downloadable document (PDF/DOCX/XLSX, etc.) with text.",
    howItWorks: ["Text explains the file; user taps to download."],
    badge: "Basic",
    source: "Help file — Nodes (p.4)",
    tags: ["download"],
    previewImageKey: "Documents + Text",
  },
  {
    title: "Button",
    icon: <SmartButtonIcon />,
    definition: "Up to 3 clickable buttons that send content or trigger actions.",
    howItWorks: ["Choose a Header Type to control what gets sent on tap."],
    options: [
      { label: "Text", icon: <TextFieldsIcon />, hint: "Sends a text message" },
      { label: "Image", icon: <ImageIcon />, hint: "Sends a downloadable image" },
      { label: "Document", icon: <DescriptionIcon />, hint: "Sends a downloadable doc" },
      { label: "Video", icon: <MovieIcon />, hint: "Sends/plays a video" },
    ],
    badge: "Interactive",
    source: "Help file — Button (p.5–6)",
    tags: ["cta", "options", "header type"],
    previewImageKey: "Button",
  },
  {
    title: "List",
    icon: <FormatListBulletedIcon />,
    definition: "Dropdown-style list (up to ~10 items) with a title and selectable options.",
    howItWorks: ["User taps an option to trigger the next step or action."],
    badge: "Interactive",
    source: "Help file — List (p.7)",
    tags: ["menu"],
    previewImageKey: "List",
  },
  {
    title: "CTA URL",
    icon: <LinkIcon />,
    definition: "Shows a text body alongside a button/link that opens a URL.",
    howItWorks: ["Text appears + a button with embedded URL", "Tap redirects to external site/app."],
    badge: "Interactive",
    source: "Help file — CTA URL (p.8)",
    tags: ["external link"],
    previewImageKey: "CTA URL",
  },
];

const SETTINGS_CARD: NodeBlock = {
  title: "Configure Intent (Node Settings)",
  icon: <SettingsIcon />,
  definition: "Open the ⚙️ on any node to control behavior and data mapping.",
  howItWorks: [
    "Sub Keywords — comma-separated words that also trigger the sub-bot.",
    "Result Name — key under which input is saved in webforms.",
    "Enable Status — status auto-updated when sub-bot appears.",
    "Default Parameters — predefined key/value pairs auto-filled.",
    "Click Save to apply changes.",
  ],
  badge: "Settings",
  source: "Help file — Settings (p.9–10)",
  tags: ["intent", "data mapping"],
  previewImageKey: "Configure Intent",
};

const Pill: React.FC<{ label: string }> = ({ label }) => (
  <Chip size="small" label={label} sx={{ borderRadius: 2 }} />
);

const NodeCard: React.FC<{ data: NodeBlock; previewUrl?: string }> = ({ data, previewUrl }) => (
  <Card elevation={2} sx={{ height: "100%", borderRadius: 3, overflow: "hidden" }}>
    {/* header */}
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.2,
        px: 2,
        py: 1.25,
        borderBottom: (t) => `1px solid ${t.palette.divider}`,
        background: "linear-gradient(90deg, rgba(99,102,241,0.12), rgba(236,72,153,0.12))",
      }}
    >
      <Box sx={{ display: "grid", placeItems: "center" }}>{data.icon}</Box>
      <Typography fontWeight={700}>{data.title}</Typography>
      <Box sx={{ ml: "auto" }}>{data.badge && <Pill label={data.badge} />}</Box>
    </Box>

    {/* preview image */}
    {previewUrl && (
      <CardMedia
        component="img"
        image={previewUrl}
        alt={`${data.title} preview`}
        sx={{ maxHeight: 180, objectFit: "cover" }}
      />
    )}

    <CardContent sx={{ pt: 1.5 }}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {data.definition}
      </Typography>

      {data.howItWorks?.length ? (
        <>
          <Typography variant="caption" sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
            <InfoOutlinedIcon fontSize="small" /> How it works
          </Typography>
          <List dense sx={{ pt: 0, mb: 0.5 }}>
            {data.howItWorks.map((h, i) => (
              <ListItem key={i} sx={{ py: 0.3 }}>
                <ListItemText primaryTypographyProps={{ variant: "body2" }} primary={`• ${h}`} />
              </ListItem>
            ))}
          </List>
        </>
      ) : null}

      {data.options?.length ? (
        <>
          <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
            Options
          </Typography>
          <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", mt: 0.5 }}>
            {data.options.map((opt) => (
              <Chip key={opt.label} icon={opt.icon as any} label={opt.hint ? `${opt.label} — ${opt.hint}` : opt.label} size="small" sx={{ mb: 1 }} />
            ))}
          </Stack>
        </>
      ) : null}

      <Stack direction="row" spacing={0.75} sx={{ mt: 0.5, flexWrap: "wrap" }}>
        {data.tags?.map((t) => (
          <Chip key={t} size="small" variant="outlined" label={t} sx={{ mb: 1 }} />
        ))}
        {data.source && <Chip size="small" variant="outlined" label={data.source} sx={{ mb: 1 }} />}
      </Stack>
    </CardContent>
  </Card>
);

const SectionHeader: React.FC<{ title: string; subtitle?: string; id?: string }> = ({ title, subtitle, id }) => (
  <Box id={id} sx={{ mb: 1.25 }}>
    <Typography variant="subtitle2" sx={{ opacity: 0.85 }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="caption" color="text.secondary">
        {subtitle}
      </Typography>
    )}
  </Box>
);

const BotFlowHelp: React.FC<BotFlowHelpProps> = ({ pdfs, images, nodeImages }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [gsActive, setGsActive] = useState(0);
  const [gallery, setGallery] = useState<string[]>(images ?? []);
  const [imgInput, setImgInput] = useState("");
  const [menuEl, setMenuEl] = useState<null | HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const filteredNodes = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return NODE_CARDS;
    return NODE_CARDS.filter((n) =>
      [
        n.title,
        n.definition,
        ...(n.howItWorks ?? []),
        ...(n.options?.map((o) => `${o.label} ${o.hint ?? ""}`) ?? []),
        ...(n.tags ?? []),
        n.badge ?? "",
      ]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [query]);

  const jumpTo = (hash: string) => {
    const el = contentRef.current?.querySelector(hash);
    if (el) (el as HTMLElement).scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onAddImage = () => {
    const url = imgInput.trim();
    if (!url) return;
    setGallery((g) => (g.includes(url) ? g : [url, ...g]));
    setImgInput("");
  };

  const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) => setMenuEl(e.currentTarget);
  const handleCloseMenu = () => setMenuEl(null);

  const handlePrint = () => {
  window.open(
    "https://mdimages-test.s3.amazonaws.com/2bdce5f2-b4d3-47a6-af67-b17070961b48_BOTBUILDERHelpfile.pdf",
    "_blank"
  );
};

  return (
    <div>
      <Tooltip title="Help">
        <IconButton onClick={() => setIsOpen(true)}>
          <HelpOutlineIcon fontSize="medium" />
        </IconButton>
      </Tooltip>

      <Drawer anchor="right" open={isOpen} onClose={() => setIsOpen(false)} sx={{ zIndex: 1400 }}>
        <Box
          sx={{
            width: 520,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            bgcolor: (t) => (t.palette.mode === "light" ? "#fff" : "background.paper"),
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 1.25,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: (t) => `1px solid ${t.palette.divider}`,
              position: "sticky",
              top: 0,
              zIndex: 2,
              background: "linear-gradient(90deg, rgba(99,102,241,0.16), rgba(236,72,153,0.16))",
              backdropFilter: "saturate(1.2)",
            }}
          >
            <Typography variant="h6" fontWeight={800}>
              Bot Builder • Help
            </Typography>

            <Stack direction="row" spacing={1}>
              {/* Download PDFs menu */}
              <Tooltip title="Download PDFs">
                <IconButton onClick={handleOpenMenu}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              <Menu anchorEl={menuEl} open={Boolean(menuEl)} onClose={handleCloseMenu}>
  {(pdfs && pdfs.length > 0 ? pdfs : DEFAULT_PDFS).map((p) => (
    <MenuItem
      key={p.href}
      component="a"
      href={p.href}
      target="_blank"
      rel="noopener noreferrer"
      download
      onClick={handleCloseMenu}
    >
      <PictureAsPdfIcon fontSize="small" style={{ marginRight: 8 }} />
      {p.title}
    </MenuItem>
  ))}
</Menu>
              {/* Print/Save as PDF (of the full drawer content) */}
              <Tooltip title="Print / Save as PDF">
                <IconButton onClick={handlePrint}>
                  <PictureAsPdfIcon />
                </IconButton>
              </Tooltip>

              <IconButton onClick={() => setIsOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Box>

          {/* Search + Quick Jump */}
          <Box sx={{ px: 2, pt: 2 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search nodes, options, or settings…"
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1 }} /> }}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
              <Chip label="Getting Started" size="small" variant="outlined" onClick={() => jumpTo("#sec-getting-started")} sx={{ mb: 1 }} />
              <Chip label="Nodes" size="small" variant="outlined" onClick={() => jumpTo("#sec-nodes")} sx={{ mb: 1 }} />
              <Chip label="Settings" size="small" variant="outlined" onClick={() => jumpTo("#sec-settings")} sx={{ mb: 1 }} />
              <Chip label="Button Options" size="small" onClick={() => setQuery("button options header type")} sx={{ mb: 1 }} />
              <Chip label="CTA URL" size="small" onClick={() => setQuery("cta url")} sx={{ mb: 1 }} />
              <Chip label="Gallery" size="small" variant="outlined" onClick={() => jumpTo("#sec-gallery")} sx={{ mb: 1 }} />
            </Stack>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Scrollable Content */}
          <Box ref={contentRef} sx={{ px: 2, pb: 2, overflowY: "auto" }}>
            {/* Getting Started */}
            <SectionHeader id="sec-getting-started" title="Getting Started" subtitle="Create a new flow and prepare it for building." />
            <Stepper activeStep={gsActive} alternativeLabel sx={{ mb: 2 }}>
              {GETTING_STARTED_STEPS.map((s, i) => (
                <Step key={s.title} onClick={() => setGsActive(i)}>
                  <StepLabel>{s.title}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <Accordion defaultExpanded disableGutters sx={{ mb: 2, borderRadius: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>
                  Step-by-step instructions ({GETTING_STARTED_STEPS.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
  {GETTING_STARTED_STEPS.map((s, idx) => {
    const isActive = idx === gsActive; // capture index locally
    return (
      <Box
        key={s.title}
        sx={{
          mb: 1.5,
          p: 1.25,
          border: (t) => `1px dashed ${t.palette.divider}`,
          borderRadius: 2,
          bgcolor: (t) => (isActive ? t.palette.action.hover : "transparent"),
        }}
      >
        <Typography fontWeight={600} sx={{ mb: 0.5 }}>
          {idx + 1}. {s.title}
        </Typography>
        <List dense sx={{ pt: 0 }}>
          {s.bullets.map((b, j) => (
            <ListItem key={j} sx={{ py: 0.3 }}>
              <ListItemText
                primaryTypographyProps={{ variant: "body2" }}
                primary={`• ${b}`}
              />
            </ListItem>
          ))}
        </List>
        {s.source && <Chip size="small" variant="outlined" label={s.source} />}
      </Box>
    );
  })}
  <Box sx={{ display: "flex", gap: 1 }}>
    <Button
      variant="contained"
      onClick={() =>
        setGsActive((p) =>
          Math.max(0, Math.min(p - 1, GETTING_STARTED_STEPS.length - 1))
        )
      }
    >
      Previous
    </Button>
    <Button
      variant="outlined"
      onClick={() =>
        setGsActive((p) =>
          Math.max(0, Math.min(p + 1, GETTING_STARTED_STEPS.length - 1))
        )
      }
    >
      Next
    </Button>
  </Box>
             </AccordionDetails>

            </Accordion>

            {/* Nodes */}
            <SectionHeader id="sec-nodes" title="Nodes" subtitle="Choose the right block for your message or interaction." />
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              {filteredNodes.map((n) => (
                <Grid item xs={12} sm={12} md={6} key={n.title}>
                  <NodeCard data={n} previewUrl={n.previewImageKey ? nodeImages?.[n.previewImageKey] : undefined} />
                </Grid>
              ))}
            </Grid>
            {filteredNodes.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No matching nodes. Try a different search term.
              </Typography>
            )}

            {/* Settings */}
            <SectionHeader id="sec-settings" title="Settings" subtitle="Configure Intent for each node via the ⚙️ menu." />
            <NodeCard data={SETTINGS_CARD} previewUrl={nodeImages?.[SETTINGS_CARD.previewImageKey!]} />

            {/* Image Gallery */}
            <SectionHeader id="sec-gallery" title="Image Gallery" subtitle="Preview any reference images you provide." />
            <Box sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Paste image URL and press Add"
                value={imgInput}
                onChange={(e) => setImgInput(e.target.value)}
                InputProps={{ startAdornment: <AddPhotoAlternateIcon sx={{ mr: 1 }} /> }}
              />
              <Button variant="contained" onClick={onAddImage}>Add</Button>
            </Box>
            {gallery.length > 0 ? (
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                {gallery.map((url) => (
                  <Grid item xs={12} sm={6} key={url}>
                    <Card sx={{ borderRadius: 2, overflow: "hidden" }}>
                      <CardMedia component="img" src={url} alt="help-asset" sx={{ maxHeight: 220, objectFit: "cover" }} />
                      <Box sx={{ p: 1 }}>
                        <Typography variant="caption" sx={{ wordBreak: "break-all" }}>
                          {url}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No images yet. Paste a URL above to add one.
              </Typography>
            )}

            {/* Footer Source */}
            <Box sx={{ mt: 2, px: 1.5, py: 1, borderTop: (t) => `1px solid ${t.palette.divider}`, borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Tip: Use the Download icon in the header to fetch the official help PDFs, or print this page to PDF.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </div>
  );
};

export default BotFlowHelp;
